import { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import { CHAIN_ID } from '@/constants/constants';
import type { Collections } from '@/models/Collections';
import { fetchNfts } from '@/utilities/fetchNfts';
import type { NftMetadata } from 'alchemy-sdk';

export interface BonusConfig {
  multiplierThresholdConfig: MultiplierThresholdConfig;
  rarityConfigs: RarityConfigs;
  contractAddress: string;
  maximumBonusPercentage: number;
  metadataTraitName: string;
}

interface MultiplierThresholdConfig {
  thresholds: number[];
  multipliers: number[];
}

type RarityConfigs = {
  [rarity: string]: RarityConfig;
};

interface RarityConfig {
  multiplier: number;
  color: string;
}

export interface RarityDetail {
  rarity: string;
  count: number;
  multiplier: number;
  bonusPercentage: number;
}

interface NftWithMetadata {
  metadata: NftMetadata;
}

interface Props {
  address: string | undefined;
  baseCollection: Collections | undefined;
  bonusCollection: Collections | undefined;
  basePercentage?: number | undefined;
  opportunityPublishedDate?: string | null | undefined;
  bonusConfig: BonusConfig | undefined;
}

const getRarityValue = (
  metadata: NftMetadata,
  rarityConfigs: RarityConfigs,
  metadataTraitName: string
): number => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const rarity = metadata.attributes?.[metadataTraitName] ?? 'Unknown';
  return (
    rarityConfigs[rarity]?.multiplier ?? rarityConfigs['Unknown'].multiplier
  );
};

const calculateMultiplierCount = (
  nftCount: number,
  multiplierThresholdConfig: MultiplierThresholdConfig
): number => {
  for (let i = 0; i < multiplierThresholdConfig.thresholds.length; i++) {
    if (nftCount >= multiplierThresholdConfig.thresholds[i])
      return multiplierThresholdConfig.multipliers[i];
  }
  return nftCount >= 1 ? 1 : 0;
};

const processRarityDetails = (
  sortedNfts: NftWithMetadata[],
  basePercentage: number,
  maximumBonusPercent: number,
  metadataTraitName: string,
  rarityConfigs: RarityConfigs,
  multiplierThresholdConfig: MultiplierThresholdConfig
): { details: RarityDetail[]; bonusPercentage: number } => {
  const rarityCounts: { [key: string]: { count: number; multiplier: number } } =
    Object.fromEntries(
      Object.keys(rarityConfigs).map((key) => [
        key,
        { count: 0, multiplier: 0 },
      ])
    );
  let bonusPercentage = 0;
  const multiplierCount = calculateMultiplierCount(
    sortedNfts.length,
    multiplierThresholdConfig
  );
  let details: RarityDetail[] = [];

  for (
    let i = 0;
    i < multiplierCount && bonusPercentage < maximumBonusPercent;
    i++
  ) {
    let currentMultiplier = getRarityValue(
      sortedNfts[i].metadata,
      rarityConfigs,
      metadataTraitName
    );

    const attributes = sortedNfts[i].metadata.attributes as
      | Record<string, any>
      | undefined;

    const rarityType = attributes
      ? attributes[metadataTraitName] ?? 'Unknown'
      : 'Unknown';

    if (bonusPercentage + currentMultiplier > maximumBonusPercent) {
      currentMultiplier = maximumBonusPercent - bonusPercentage;
    }

    bonusPercentage += currentMultiplier;
    rarityCounts[rarityType].count++;
    rarityCounts[rarityType].multiplier += currentMultiplier;
  }

  details = Object.entries(rarityCounts).map(
    ([rarity, { count, multiplier }]) => ({
      rarity,
      count,
      multiplier,
      bonusPercentage: (multiplier * basePercentage) / 100,
    })
  );

  // Sort results to always appear in descending rarity multiplier order, e.g. so that Mythical is always first and Common last
  details.sort((a, b) => {
    const multiplierA = rarityConfigs[a.rarity]?.multiplier ?? 0;
    const multiplierB = rarityConfigs[b.rarity]?.multiplier ?? 0;
    return multiplierB - multiplierA;
  });

  return { details, bonusPercentage };
};

const useBonusMultiplier = ({
  address,
  baseCollection,
  bonusCollection,
  basePercentage = 0,
  opportunityPublishedDate,
  bonusConfig,
}: Props) => {
  const defaultAccount = useAccount();
  const [bonusMultiplier, setBonusMultiplier] = useState(0);
  const [adjustedAmount, setAdjustedAmount] = useState(basePercentage);
  const [rarityDetails, setRarityDetails] = useState<RarityDetail[]>([]);
  const [error, setError] = useState<string | null>(null);

  address = address ?? defaultAccount?.address;

  useEffect(() => {
    const fetchData = async () => {
      if (
        !address ||
        !baseCollection ||
        !bonusCollection ||
        !opportunityPublishedDate ||
        !bonusConfig
      ) {
        setBonusMultiplier(basePercentage);
        setAdjustedAmount(basePercentage);
        setRarityDetails([]);
        return;
      }

      try {
        const twoWeeksBeforeOpportunity = new Date(opportunityPublishedDate);
        twoWeeksBeforeOpportunity.setDate(
          twoWeeksBeforeOpportunity.getDate() - 14
        );

        const bonusNfts = await fetchNfts(address, [bonusCollection], CHAIN_ID);
        const totalNfts: NftWithMetadata[] = bonusNfts.flatMap((collection) =>
          collection.nfts
            .filter((nft) => {
              if (nft.acquiredAt && nft.acquiredAt.blockTimestamp) {
                const acquiredDate = new Date(nft.acquiredAt.blockTimestamp);
                return acquiredDate <= twoWeeksBeforeOpportunity;
              }
              return false;
            })
            .map((nft) => ({ metadata: nft.rawMetadata as NftMetadata }))
        );
        const sortedNfts = totalNfts.sort(
          (a, b) =>
            getRarityValue(
              b.metadata,
              bonusConfig.rarityConfigs,
              bonusConfig.metadataTraitName
            ) -
            getRarityValue(
              a.metadata,
              bonusConfig.rarityConfigs,
              bonusConfig.metadataTraitName
            )
        );
        const { details, bonusPercentage } = processRarityDetails(
          sortedNfts,
          basePercentage,
          bonusConfig.maximumBonusPercentage,
          bonusConfig.metadataTraitName,
          bonusConfig.rarityConfigs,
          bonusConfig.multiplierThresholdConfig
        );
        const adjustedMultiplier = 1 + bonusPercentage / 100;
        setRarityDetails(details);
        setBonusMultiplier(adjustedMultiplier);
        setAdjustedAmount(
          basePercentage + (basePercentage * bonusPercentage) / 100
        );
      } catch (err: any) {
        setError('Error calculating bonus multiplier');
      }
    };

    void fetchData();
  }, [
    address,
    bonusCollection,
    baseCollection,
    basePercentage,
    opportunityPublishedDate,
    bonusConfig,
  ]);

  return { bonusMultiplier, adjustedAmount, rarityDetails, error };
};

export default useBonusMultiplier;
